/*
 * THE STATECHART, WRITTEN IN FRONT OF YOU — and then running.
 *
 * `models.write` is the one call on this platform whose ARGUMENT is the product: the assistant
 * authors SCXML, and that document is the deliverable. It drew as a collapsed row named after a
 * function, with the document folded away as escaped JSON and arriving whole when the call ended —
 * so the most substantial thing the assistant does looked like its least, and a reader watching a
 * model being written saw twenty seconds of nothing followed by a line of text.
 *
 * This is the body for that card. Two states, one card:
 *
 *   · WHILE IT IS BEING WRITTEN — the document, coloured, growing a fragment at a time. The
 *     highlighter works on text rather than on a parse for exactly this reason: half a statechart
 *     is not valid XML and would otherwise show as nothing until the last character.
 *   · ONCE IT LANDS — the statechart itself, the same renderer the model page uses, with the live
 *     active state on it if the model is already running. The card stops being a transcript of the
 *     writing and becomes a view of the thing written.
 *
 * The skeleton around both — the frame, the title, the status beside it — lives in base-ui and is
 * shared with every other work card. Only what is inside is ours, because base-ui has no business
 * knowing what a statechart is. See work-card.ts.
 */
import { html, nothing } from 'lit';
import type { TemplateResult } from 'lit';
import { registerWorkCard, registerFenceRenderer, renderXml, type WorkView, partialString } from '@vitesse/base-ui/ai';
import { t } from '@vitesse/base-ui/i18n';
import { STATECHART_INLINE_STYLES } from '../shell/statechart-inline-styles';
import { simulationById } from './model-simulate-tool';
// Re-exported: the tests and the tools beside this file read it from here.
export { partialString };
import type { TurnTool } from '@vitesse/base-ui/ai';
import type { GatewayScope } from './gateway-resolve';


/*
 * THE PREVIEW'S OWN STYLING, because the transcript is a SHADOW ROOT.
 *
 * `<vitesse-statechart>` renders into the LIGHT DOM on purpose: on the model page it is styled by
 * the app's global `_statechart-renderer.scss`. Inside the AI transcript it is not on the page — it
 * is inside base-ui's shadow tree, which that stylesheet cannot reach. So the SVG rendered with no
 * fills, no strokes and no text colour: a solid black rectangle, which is exactly what was
 * reported. The self-contained set that fixes it lives in shell/statechart-inline-styles.ts,
 * because the replay viewer needs the identical set for the identical reason.
 */
const previewStyles = STATECHART_INLINE_STYLES;

/** The preview itself — the chart, and the styling it needs to survive the shadow boundary. */
function chartPreview(scxml: string, active: string): TemplateResult {
  return html`<div class="wc-chart">${previewStyles}<vitesse-statechart .scxml=${scxml} .active=${active}></vitesse-statechart></div>`;
}

/**
 * Claim `models.write`.
 *
 * `deps` is the same gateway scope the model tools use, so the card can find the model afterwards
 * and show which state it is actually in. Returns the undo, like every other registration here.
 */
export function registerModelWorkCard(deps: GatewayScope): () => void {
  /*
   * AND THE SAME CARD FOR A STATECHART THAT ARRIVES AS PROSE.
   *
   * An agent asked to author a model writes it through `models.write` and gets the card above. An
   * agent asked for the SOURCE returns the identical document in a ```scxml fence — and that
   * rendered as a wall of escaped XML in a plain code block. Two renderings of one thing, with
   * nothing on screen to explain why one of them is a live chart and the other is not.
   *
   * The fence is not a MODEL — nothing was written anywhere, so there is no id and no live state —
   * so it draws as the document and its shape, which is exactly what it is: source somebody can
   * read, plus a picture of what it does.
   */

  const liveState = (modelId: string): string => {
    for (const entry of deps.entries()) {
      const m = entry.models?.all?.find((x: { id: string }) => x.id === modelId);
      if (m) return String((m as { currentState?: string }).currentState ?? '');
    }
    return '';
  };

  /*
   * ONE CARD, TWO VIEWS. The model and its source were two components — a chart card from the
   * tool call, a code block from a prose fence — and a reader had no way to get from one to the
   * other. Now it is one card with the model view and the code view behind the same toggle every
   * other control in the product uses, whichever way the document arrived.
   */
  const card = {
    views: true,
    title: (tool: TurnTool) => {
      const name = partialString(tool.argumentsJson ?? '', 'name');
      const id = partialString(tool.argumentsJson ?? '', 'modelId');
      // The name is optional on an edit — the id is what always exists, and naming it beats a card
      // titled with a placeholder.
      return name || id || 'Statechart';
    },
    // A rename carries no document; a card whose body is empty is worse than the row it replaced.
    when: (tool: TurnTool) => partialString(tool.argumentsJson ?? '', 'scxml').length > 0,
    body: (tool: TurnTool, view: WorkView) => {
      const scxml = partialString(tool.argumentsJson ?? '', 'scxml');
      if (!scxml) return nothing;
      // The source while it is still arriving or was refused — on a refusal the text is the
      // evidence to read the relay's reason against — and whenever the reader asks for it.
      if (view === 'code') return renderXml(scxml);
      const id = partialString(tool.argumentsJson ?? '', 'modelId');
      return chartPreview(scxml, liveState(id));
    },
  };
  const offCard = registerWorkCard('models.write', card);
  // The same card for a statechart that arrives as prose: a ```scxml fence is presented to it as
  // a settled call whose one argument is the source, under the key the tool would have used.
  const offFence = registerFenceRenderer('scxml', card, 'scxml');

  /*
   * THE SAME STATECHART, REPLAYED — the card for `models.simulate`.
   *
   * The written card shows the document and, once it lands, the chart with its LIVE state. This one
   * shows the chart with its REPLAYED state: the viewer from the model page's Simulate panel, with
   * the scrubber, the firing edges and the lanes, drawn from the trace the tool kept in memory and
   * named in its outcome. Same views toggle, same source behind it, so a person reads "written" and
   * "replayed" as two views of one thing rather than two inventions.
   *
   * REFUSALS ARE SHOWN, not folded away. A replay refused — an inert document, a controller with no
   * history — is the finding, and the frame's "failed" word alone would leave the reader to open
   * the row for the sentence. The sentence goes in the body, under the source it is about.
   *
   * AFTER A RELOAD THE PICTURE IS GONE and the card says so: the trace lived in this tab and was never
   * persisted, the summary and the source are archived with the call, and a card that quietly
   * drew an unlit chart in place of the replay would read as a replay in which nothing moved.
   */
  const note = (text: string) => (text ? html`<p class="wc-note">${text}</p>` : nothing);
  const simulateCard = {
    views: true,
    title: (tool: TurnTool) => partialString(tool.argumentsJson ?? '', 'modelId') || t('model.simulateCard'),
    body: (tool: TurnTool, view: WorkView) => {
      const traceId = (tool.data as { traceId?: string } | undefined)?.traceId ?? '';
      const kept = traceId ? simulationById(traceId) : undefined;
      // The draft as it streams in, else the stored source the tool replayed — a modelId call
      // carries no document of its own.
      const scxml = partialString(tool.argumentsJson ?? '', 'scxml') || kept?.scxml || '';
      if (tool.state === 'running' || tool.state === 'unknown') return scxml ? renderXml(scxml) : note(t('model.simulateRunning'));
      if (tool.state === 'error') return html`${scxml ? renderXml(scxml) : nothing}${note(tool.message ?? '')}`;
      if (view === 'code') return scxml ? renderXml(scxml) : note(tool.message ?? '');
      if (kept) return html`<div class="wc-chart"><vitesse-statechart-replay .scxml=${kept.scxml} .result=${kept.result}></vitesse-statechart-replay></div>`;
      return html`${scxml ? chartPreview(scxml, '') : nothing}${note(tool.message ?? '')}${note(t('model.simulateGone'))}`;
    },
  };
  const offSimulate = registerWorkCard('models.simulate', simulateCard);
  return () => {
    offCard();
    offFence();
    offSimulate();
  };
}
